/* eslint-disable import/no-cycle */
import type { Modifier, Options, Placement, State } from '@popperjs/core';
import type { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import * as React from 'react';
import { usePopper } from 'react-popper';
import * as TypeStyle from 'typestyle';
import { StatusIcon } from '../../icons/StatusIcons';
import { autoFocus } from '../../utils/AutoFocus';
import { makeTestId } from '../../utils/Declarative';
import { generateId, timeoutFocus } from '../../utils/DomUtil';
import { PropsAndElementAttrs, useGlobalPointerDownOutsideListener } from '../../utils/ReactHelpers';
import { FocusLockContext, StackingComponentWrapper, StackingRootContext } from '../../utils/StackingComponent';
import { ButtonWidget } from '../Button/ButtonWidgetShim';
import { CloseButtonWidget } from '../Button/CloseButtonWidget';
import { PopupContainerStyle } from '../Dropdown/DropdownButtonStyles';
import * as Styles from '../Popover/PopoverStyles';
import { PlacementAttributeName } from '../Popover/PopoverStyles';
import { PopoverProps } from './PopoverWidget';

export type PopoverBaseProps = Omit<PopoverProps, 'triggerElement'> & {
  /**
   * Callback to be fired when a close action occurs, for example pressing `esc`, clicking the close button, or clicking outside the popover.
   * You will be responsible for actually removing the Popover in response to this firing.
   */
  onClose: () => void;

  /**
   * The element which the popover will positionally anchor to.
   * `aria-controls` and `aria-haspopup` will be overridden on this element.
   */
  triggerElement: HTMLElement;
};

const TitleBarIconSize = 16;
const NubbinHeight = 8;
const NubbinMargin = 8;

/**
* Shifts the popover away from the trigger element to maintain 8px of space between the 2 elements no matter which side the popever is anchored to.
* {@link https://popper.js.org/docs/v2/modifiers/offset/ popper docs}
* `skidding` shifts the popup parallel to the `placement` edge of the trigger
* `distance` shifts the popup perpendicular to the `placement` edge of the trigger
*/
const PopperOffsetFunction: OffsetsFunction = (data) => {
  const distance = NubbinHeight + NubbinMargin;
  let skidding = 0;
  const dimensionToSkid = data.placement.includes('bottom') || data.placement.includes('top') ? data.reference.width : data.reference.height;
  if (data.placement.includes('start')) {
    skidding = dimensionToSkid / 2 - 20;
  } else if (data.placement.includes('end')) {
    skidding = 20 - dimensionToSkid / 2;
  }

  return [skidding, distance];
};

/**
 * Hides the nubbin when it can no longer line up with the trigger.
 * This is mostly relevant for the case of `left` or `right` placement when the titlebar is shown.
 */
const applyNubbinHide: Modifier<'applyNubbinHide', Options> = {
  name: 'applyNubbinHide',
  enabled: true,
  phase: 'write',
  fn({ state }: { state: State }): void {
    const { arrow } = state.elements;
    if (arrow) {
      if (state.modifiersData.arrow?.centerOffset !== 0) {
        arrow.classList.add('hidden');
      } else {
        arrow.classList.remove('hidden');
      }
    }
  }
};

export const PopoverBase: React.FC<PropsAndElementAttrs<PopoverBaseProps>> = props => {
  // eslint-disable-next-line max-len, deprecation/deprecation
  const { alertType, className, darkMode, disableAutoFocus, footerButtons, getElementToRefocus, onClose, onKeyUp, placement, popoverContent, portalEventTrapOverrides, showTitleBar, stackingRootId, style, testId, title, triggerElement, width, zIndex, ...htmlAttrs } = props;

  const [nubbinElement, setNubbinElement] = React.useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(null);
  const [stackingRootEl, setStackingRootEl] = React.useState<HTMLDivElement | null>(null);
  const [titleBarId] = React.useState<string>(generateId());

  // autofocus something in the popup if it just opened, and refocus the previous element when it closes
  React.useEffect(() => {
    if (!popperElement) { return undefined; }

    autoFocus(disableAutoFocus, popperElement, true);

    // When the popover closes and there is a customized element that should receive focus,
    // we need to return focus in a timeout because focus is still trapped when the cleanup function gets called
    // https://www.npmjs.com/package/react-focus-lock#unmounting-and-focus-management
    // eslint-disable-next-line deprecation/deprecation
    return () => timeoutFocus(getElementToRefocus?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popperElement]);

  // watch global pointer events to dismiss the popup once it is shown
  useGlobalPointerDownOutsideListener(triggerElement, popperElement, onClose);

  const titleBarVisible = showTitleBar || alertType;

  if (!titleBarVisible && typeof title !== 'string') {
    throw new Error('`title` must be a string if there is no visible titlebar');
  }

  /**
   * Custom modifier used to prevent the nubbin from spanning across both the titlebar and the body.
   * Note: needs to be memoized, or else it creates an infinite loop in popper core.
   */
  const arrowModifier = React.useMemo(() => ({
    name: 'arrow',
    options: {
      element: nubbinElement,
      padding: (modifierProps: { placement: Placement }) => {
        let topPadding = NubbinMargin;

        if ((modifierProps.placement === 'left' || modifierProps.placement === 'right') && titleBarVisible) {
          topPadding = 40;
        }
        return {
          top: topPadding,
          right: NubbinMargin,
          bottom: NubbinMargin,
          left: NubbinMargin,
        };
      }
    }
  }), [nubbinElement, titleBarVisible]);

  const { styles, attributes } = usePopper(triggerElement, popperElement, {
    modifiers: [
      arrowModifier,
      applyNubbinHide,
      {
        name: 'offset',
        options: {
          offset: PopperOffsetFunction,
        }
      }],
    placement,
  });

  const activePlacement = attributes.popper?.[PlacementAttributeName];
  const nubbinIntersectsTitlebar = activePlacement?.includes('bottom') || activePlacement?.includes('left-start') || activePlacement?.includes('right-start');
  const nubbinHasColor = titleBarVisible && nubbinIntersectsTitlebar;

  const handleKeyUp = React.useCallback<React.KeyboardEventHandler<HTMLElement>>(e => {
    if (e.key === 'Escape') {
      onClose();
      e.preventDefault();
      e.stopPropagation();
    }
    onKeyUp?.(e);
  }, [onClose, onKeyUp]);

  return <FocusLockContext.Provider value={{ enableFocusLock: true, returnFocus: !getElementToRefocus }}>
    <StackingRootContext.Consumer>{stackingRoot => <>
      {!stackingRootId && !stackingRoot && <div style={PopupContainerStyle} ref={setStackingRootEl} />}
      <StackingRootContext.Provider value={stackingRoot || stackingRootEl}>
        <StackingComponentWrapper
          portalEventTrapOverrides={portalEventTrapOverrides}
          stackingRootId={stackingRootId}
          wrapperTestId={makeTestId(testId, 'PopoverWidget')}
          zIndex={zIndex}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            {...(titleBarVisible ? { 'aria-labelledby': titleBarId } : { 'aria-label': title.toString() })}
            data-tb-test-id={makeTestId(testId, 'PopoverWidget')}
            role='dialog'
            {...htmlAttrs}
            className={TypeStyle.classes(Styles.PopoverStyleName, { darkMode, titleBarVisible }, className)}
            onKeyUp={handleKeyUp}
            ref={setPopperElement}
            style={{ width, ...styles.popper, ...style }}
            {...attributes.popper}
          >
            {titleBarVisible && <div className={TypeStyle.classes(Styles.TitleBarStyleName, alertType)} id={titleBarId}>
              <div>
                {alertType && <StatusIcon style={{ marginRight: 4 }} size={TitleBarIconSize} type={alertType} />}<span>{title}</span>
              </div>
            </div>}
            <CloseButtonWidget
              data-tb-test-id={makeTestId(testId, 'PopoverWidget', 'CloseButton')}
              onClick={onClose}
              style={Styles.CloseButtonStyle}
              useDarkStyling={darkMode}
            />
            <div
              className={TypeStyle.classes(Styles.NubbinStyleName, { nubbinHasColor }, alertType)}
              ref={setNubbinElement}
              style={styles.arrow}
              {...(activePlacement && { [PlacementAttributeName]: activePlacement })}
            />
            <div className={TypeStyle.classes(Styles.BodyStyleName, { titleBarVisible })}>
              {popoverContent}
            </div>
            {footerButtons && <div className={Styles.FooterStyleName}>{footerButtons.map((buttonProps, index) =>
              <ButtonWidget key={index} {...buttonProps} />
            )}</div>}
          </div>
        </StackingComponentWrapper>
      </StackingRootContext.Provider>
    </>}
    </StackingRootContext.Consumer>
  </FocusLockContext.Provider>;
};
PopoverBase.displayName = 'PopoverBase';
